import { motion } from "framer-motion";
import {
  BookOpen, FileText, Layout, Palette, Eye, FileCode2, Sparkles, ArrowUpRight,
} from "lucide-react";

const services = [
  {
    icon: BookOpen,
    color: "#1F5EFF",
    title: "Digital Publishing",
    desc: "EPUB2, EPUB3, HTML5, Kindle, interactive & digital textbooks — fixed-layout and reflowable.",
    items: ["EPUB2 / EPUB3", "HTML5", "Kindle (KF8)", "Interactive eBooks", "Fixed Layout", "Reflowable", "Digital Textbooks"],
  },
  {
    icon: FileText,
    color: "#6D28D9",
    title: "Content Management",
    desc: "Editorial, copyediting, proofreading, composition, project management, metadata & indexing.",
    items: ["Editorial", "Copyediting", "Proofreading", "Composition", "Project Management", "Metadata", "Indexing", "Print-on-Demand"],
  },
  {
    icon: Layout,
    color: "#00B8A9",
    title: "Typesetting",
    desc: "Books, journals, magazines and educational, scientific, legal & professional publishing.",
    items: ["Books", "Journals", "Magazines", "Educational", "Scientific", "Legal", "Adobe InDesign", "XML Workflows"],
  },
  {
    icon: Palette,
    color: "#1F5EFF",
    title: "Graphics Studio",
    desc: "Illustration, redraw, scanning, infographics, scientific artwork & creative cover design.",
    items: ["Illustration", "Image Enhancement", "Redraw", "Scanning", "Infographics", "Scientific Artwork", "Cover Design"],
  },
  {
    icon: Eye,
    color: "#6D28D9",
    title: "Accessibility",
    desc: "WCAG 2.2, ADA-compliant PDFs, Word, PPT, JAWS testing, audits & remediation.",
    items: ["WCAG 2.2", "ADA Compliance", "Accessible PDF/Word/PPT", "Alt Text", "Long Descriptions", "Closed Captioning", "JAWS Testing"],
  },
  {
    icon: FileCode2,
    color: "#00B8A9",
    title: "Digital Conversion",
    desc: "XML, DTD, OCR, legacy & PDF conversion, content tagging, metadata & digitization.",
    items: ["XML / DTD", "OCR", "Legacy Conversion", "PDF Conversion", "SGML", "Content Tagging", "Archive Digitization"],
  },
  {
    icon: Sparkles,
    color: "#1F5EFF",
    title: "Interactive Learning",
    desc: "Interactive textbooks, HTML5 learning, rich media, voice, video & assessments.",
    items: ["Interactive Textbooks", "HTML5 Learning", "Voice & Video", "Assessments", "Rich Media", "Flash to HTML5"],
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="overline">What we do</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B1F3A] tracking-tighter leading-[1.08]">
              Services that move publishing forward.
            </h2>
          </div>
          <p className="text-[#475569] text-base lg:max-w-md">
            Seven specialised practices working as one — built to scale across formats, languages, and markets.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.title}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                className="lift-card group relative overflow-hidden rounded-3xl border border-[#0B1F3A]/10 bg-white p-7"
              >
                <div
                  className="absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-50 blur-2xl group-hover:opacity-70 transition-opacity"
                  style={{ backgroundColor: `${s.color}30` }}
                />
                <div
                  className="relative inline-flex h-14 w-14 items-center justify-center rounded-2xl mb-5 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${s.color}14`, color: s.color }}
                >
                  <Icon className="h-7 w-7" />
                </div>
                <h3 className="relative font-display font-bold text-xl text-[#0B1F3A]">{s.title}</h3>
                <p className="relative mt-2 text-[14px] text-[#475569] leading-relaxed">{s.desc}</p>

                <div className="relative mt-5 flex flex-wrap gap-1.5">
                  {s.items.slice(0, 6).map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-[#F0F8FF] text-[#0B1F3A]/80 text-[11px] px-2.5 py-1 border border-[#0B1F3A]/5"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <a
                  href="#contact"
                  aria-label={`Discuss ${s.title} with our team`}
                  className="relative mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0B1F3A] hover:text-[#1F5EFF] focus-visible:text-[#1F5EFF] transition-colors"
                >
                  Discuss this service <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
